/**
 * SecuritySettingsScreen — keypair-lifecycle actions for the accountant.
 *
 * Two operations live here:
 *
 *  1. Passphrase rotation (routine). Unwrap O_sk with the old passphrase,
 *     re-wrap with a new one, keep the same O_pk. Cert stays valid.
 *
 *  2. Full keypair reset (compromise recovery). Generate fresh (O_pk,
 *     O_sk), wrap with a new passphrase, replace both columns on the
 *     account row. Server auto-clears the cert because O_pk changed;
 *     operator must re-sign. Files uploaded under the OLD key become
 *     unreadable — this is destructive and gated behind hard warnings.
 *
 * The drop-directory picker and unlock flow live in
 * KeypairWizardScreen — this screen assumes the tab is already unlocked
 * (route is rendered behind the AuthedShell + isUnlocked guard).
 */

import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";

import {
  base64,
  derivePassphraseSecret,
  encodePassphrase,
  freshSalt,
  generateKeypair,
  prependSalt,
  splitSaltAndWrapped,
  unwrapSecretKey,
  wrapSecretKey,
  zero,
} from "@dovach/shared-crypto";

import { ApiError, api } from "../lib/api";
import { clearOsk, setOsk } from "../lib/session-key";

export function SecuritySettingsScreen() {
  return (
    <Stack maw={640}>
      <Title order={1}>אבטחה</Title>
      <LoginPasswordCard />
      <PassphraseRotateCard />
      <KeypairResetCard />
    </Stack>
  );
}

// ────────────────────────────────────── login password change
// Different concept from the keypair passphrase below — this is the
// email-and-password you use to LOG IN. Server-side it updates the
// argon2id hash and revokes every OTHER active session for this
// account (your current tab stays alive). A notification email goes
// to the account address so a hijacker rotating it isn't silent.

function LoginPasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setSuccessMsg(null);
    if (next.length < 12) {
      setError("הסיסמה החדשה חייבת להיות באורך 12 תווים לפחות.");
      return;
    }
    if (next !== next2) {
      setError("הסיסמאות החדשות אינן תואמות.");
      return;
    }
    if (next === current) {
      setError("הסיסמה החדשה זהה לישנה.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.changePassword(current, next);
      setSuccessMsg(
        r.sessions_revoked > 0
          ? `הסיסמה הוחלפה. נותקת מ-${r.sessions_revoked} מכשירים אחרים.`
          : "הסיסמה הוחלפה.",
      );
      setCurrent("");
      setNext("");
      setNext2("");
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.detail === "invalid_current_password") {
          setError("הסיסמה הנוכחית שגויה.");
        } else if (e.detail === "new_password_same_as_old") {
          setError("הסיסמה החדשה זהה לישנה.");
        } else {
          setError(e.detail);
        }
      } else {
        setError("שגיאה לא צפויה.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group justify="space-between">
          <Title order={3}>סיסמת התחברות</Title>
          <Badge color="blue" variant="light">
            כניסה לדשבורד
          </Badge>
        </Group>
        <Text c="dimmed" size="sm">
          זו הסיסמה שאת/ה מקליד/ה במסך ההתחברות. שינוי הסיסמה ינתק את כל
          ההתחברויות האחרות שלך מהמערכת (פרט לטאב הנוכחי). תישלח גם הודעת
          דוא"ל על השינוי.
        </Text>
        <PasswordInput
          label="סיסמה נוכחית"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <PasswordInput
          label="סיסמה חדשה (לפחות 12 תווים)"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <PasswordInput
          label="סיסמה חדשה (אישור)"
          value={next2}
          onChange={(e) => setNext2(e.target.value)}
        />
        {error && <Alert color="red">{error}</Alert>}
        {successMsg && <Alert color="green">{successMsg}</Alert>}
        <Group justify="flex-end">
          <Button
            onClick={submit}
            loading={busy}
            disabled={!current || !next || !next2}
          >
            החלף סיסמה
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// ────────────────────────────────────── passphrase rotation (routine)

function PassphraseRotateCard() {
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const submit = async () => {
    setError(null);
    setSuccess(false);
    if (newPass.length < 12) {
      setError("הסיסמה החדשה חייבת להיות באורך 12 תווים לפחות.");
      return;
    }
    if (newPass !== newPass2) {
      setError("הסיסמאות החדשות אינן תואמות.");
      return;
    }
    if (newPass === oldPass) {
      setError("הסיסמה החדשה זהה לישנה.");
      return;
    }

    setBusy(true);
    try {
      // 1. Pull the wrapped blob + current pk.
      const stored = await api.keypair();
      if (!stored.wrapped_o_sk_b64 || !stored.office_public_key) {
        setError("לא נמצא מפתח שמור.");
        return;
      }

      // 2. Unwrap with OLD passphrase.
      const bundle = base64.decode(stored.wrapped_o_sk_b64);
      const split = splitSaltAndWrapped(bundle);
      const oldSecret = await derivePassphraseSecret(
        encodePassphrase(oldPass), split.salt,
      );
      let sk: Uint8Array;
      try {
        sk = await unwrapSecretKey(split.wrappedBlob, oldSecret);
      } catch {
        zero(oldSecret);
        setError("הסיסמה הנוכחית שגויה.");
        return;
      }
      zero(oldSecret);

      // 3. Re-wrap with NEW passphrase + fresh salt.
      const newSalt = freshSalt();
      const newSecret = await derivePassphraseSecret(
        encodePassphrase(newPass), newSalt,
      );
      const newWrapped = await wrapSecretKey(sk, newSecret);
      const newBundle = prependSalt(newSalt, newWrapped);

      // 4. POST to the rotate route — server keeps the same O_pk + cert.
      await api.keypairPassphraseRotate(
        stored.office_public_key,
        base64.encode(newBundle),
      );

      // 5. Refresh the in-memory session key.
      setOsk(sk);
      zero(newSecret);
      setSuccess(true);
      setOldPass("");
      setNewPass("");
      setNewPass2("");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`שגיאה: ${err.detail}`);
      } else {
        setError("רוטציית הסיסמה נכשלה.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="lg">
      <Stack>
        <Group>
          <Title order={3}>החלפת סיסמת הצפנה</Title>
          <Badge color="blue">שגרתי</Badge>
        </Group>
        <Text c="dimmed" size="sm">
          זוהי החלפת הסיסמה בלבד. מפתח ההצפנה עצמו לא משתנה, החתימה של
          המפעיל נשארת בתוקף, ולקוחות יכולים להמשיך להעלות קבצים מיד ללא
          הפרעה.
        </Text>
        <PasswordInput
          label="סיסמה נוכחית"
          value={oldPass}
          onChange={(e) => setOldPass(e.currentTarget.value)}
          autoComplete="current-password"
          dir="ltr"
        />
        <PasswordInput
          label="סיסמה חדשה (≥12 תווים)"
          value={newPass}
          onChange={(e) => setNewPass(e.currentTarget.value)}
          autoComplete="new-password"
          dir="ltr"
        />
        <PasswordInput
          label="אישור סיסמה חדשה"
          value={newPass2}
          onChange={(e) => setNewPass2(e.currentTarget.value)}
          autoComplete="new-password"
          dir="ltr"
        />
        {error && <Text c="red" size="sm">{error}</Text>}
        {success && (
          <Alert color="green">
            הסיסמה הוחלפה. הסיסמה החדשה תידרש בכניסה הבאה לדפדפן.
          </Alert>
        )}
        <Group>
          <Button
            onClick={submit}
            loading={busy}
            disabled={
              oldPass.length < 1 ||
              newPass.length < 12 ||
              newPass !== newPass2
            }
          >
            החלף סיסמה
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// ────────────────────────────────── full keypair reset (compromise)

function KeypairResetCard() {
  const [confirmText, setConfirmText] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // The accountant must type this exact phrase before the destructive
  // button arms — same pattern as GitHub repo-deletion confirmations.
  const REQUIRED_CONFIRM = "אני מבין שקבצים ישנים יהפכו לבלתי קריאים";

  const armed =
    confirmText.trim() === REQUIRED_CONFIRM &&
    newPass.length >= 12 &&
    newPass === newPass2;

  const submit = async () => {
    setError(null);
    setSuccess(false);
    if (!armed) return;

    setBusy(true);
    try {
      // Generate a fresh keypair.
      const kp = generateKeypair();
      const salt = freshSalt();
      const secret = await derivePassphraseSecret(
        encodePassphrase(newPass), salt,
      );
      const wrapped = await wrapSecretKey(kp.secretKeyBytes, secret);
      const bundle = prependSalt(salt, wrapped);

      // POST to /enroll — server detects the O_pk change and clears the cert.
      await api.keypairEnroll(kp.publicKeyHex, base64.encode(bundle));

      // Update the in-memory O_sk so the pull loop uses the new key
      // immediately (won't find any old-key ciphertext on /pending —
      // those rows died with the cert).
      setOsk(kp.secretKeyBytes);
      zero(secret);

      setSuccess(true);
      setConfirmText("");
      setNewPass("");
      setNewPass2("");
    } catch (err) {
      // On failure, fall back to a locked state — easier to recover from
      // a fresh login than from a half-rotated tab.
      clearOsk();
      if (err instanceof ApiError) {
        setError(`שגיאה: ${err.detail}`);
      } else {
        setError("איפוס המפתח נכשל.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card withBorder padding="lg" style={{ borderColor: "#fa5252" }}>
      <Stack>
        <Group>
          <Title order={3}>איפוס מפתח הצפנה (אזור מסוכן)</Title>
          <Badge color="red">הרסני</Badge>
        </Group>
        <Alert color="red" title="קבצים קיימים יהפכו לבלתי קריאים">
          איפוס המפתח יוצר זוג חדש לחלוטין. <strong>כל קובץ שהועלה בעבר
          ימשיך להיות מוצפן עם המפתח הישן ולא ניתן יהיה לפענח אותו לעולם.</strong>
          השתמש בפעולה זו רק אם יש חשד לדליפת מפתח. למקרים רגילים השתמש
          בהחלפת סיסמה למעלה.
        </Alert>
        <Alert color="yellow">
          לאחר האיפוס המפעיל יצטרך לחתום מחדש על המפתח החדש לפני שלקוחות
          יוכלו להעלות קבצים חדשים. ביניהם הדאשבורד יציג את מסך ההמתנה.
        </Alert>
        <TextInput
          label={`להוכחת הבנה, הקלד: "${REQUIRED_CONFIRM}"`}
          value={confirmText}
          onChange={(e) => setConfirmText(e.currentTarget.value)}
          placeholder={REQUIRED_CONFIRM}
        />
        <PasswordInput
          label="סיסמה חדשה למפתח החדש (≥12 תווים)"
          value={newPass}
          onChange={(e) => setNewPass(e.currentTarget.value)}
          autoComplete="new-password"
          dir="ltr"
        />
        <PasswordInput
          label="אישור סיסמה חדשה"
          value={newPass2}
          onChange={(e) => setNewPass2(e.currentTarget.value)}
          autoComplete="new-password"
          dir="ltr"
        />
        {error && <Text c="red" size="sm">{error}</Text>}
        {success && (
          <Alert color="green">
            המפתח אופס. המתן לחתימת המפעיל לפני שלקוחות יעלו קבצים חדשים.
          </Alert>
        )}
        <Group>
          <Button
            color="red"
            onClick={submit}
            loading={busy}
            disabled={!armed}
          >
            איפוס מפתח
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
